import * as React from "react";
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { DeleteMenuItem } from "components/bootstrap";
import { ConfirmDialog, Timestamp } from "components/common";
import { MoreActions } from "components/common/EntityDataTable";
import PaginatedEntityTable from "components/common/PaginatedEntityTable";
import UserNotification from "util/UserNotification";
import usePermissions from "hooks/usePermissions";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "telemetry/Constants";
import { fetchWatchlistEntries, removeWatchlistEntries } from "watchlist/hooks/useWatchlistsV2";
import { COLUMN_WIDTH } from "components/common/EntityDataTable/Constants";
import AddWatchlistEntriesModal from "./AddWatchlistEntriesModal";
import WatchlistEntriesBulkActions from "./WatchlistEntriesBulkActions";
const ENTRY_TABLE_ID = "watchlist-entries";
const COLUMNS = ["value", "expires_at"];
const SORT_FIELDS = { value: "key", expires_at: "expire_after" };
const ATTRIBUTES = [
  { id: "value", title: "Value", sortable: true },
  { id: "expires_at", title: "Expires", sortable: true }
];
const DEFAULT_LAYOUT = {
  entityTableId: ENTRY_TABLE_ID,
  defaultPageSize: 50,
  defaultSort: { attributeId: "value", direction: "asc" },
  defaultDisplayedAttributes: COLUMNS,
  defaultColumnOrder: COLUMNS
};
const columnRenderers = {
  attributes: {
    value: {
      renderCell: (_value, entry) => /* @__PURE__ */ React.createElement("span", null, entry.value)
    },
    expires_at: {
      renderCell: (_expiresAt, entry) => entry.expires_at ? /* @__PURE__ */ React.createElement(Timestamp, { dateTime: entry.expires_at }) : /* @__PURE__ */ React.createElement("span", null, "No expiry"),
      staticWidth: COLUMN_WIDTH.date
    }
  }
};
const WatchlistEntries = ({ watchlistId, watchlistName, showAddModal, onCloseAddModal }) => {
  const queryClient = useQueryClient();
  const sendTelemetry = useSendTelemetry();
  const { isPermitted } = usePermissions();
  const canDelete = isPermitted("watchlist:delete");
  const [deletingEntry, setDeletingEntry] = useState(null);
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["watchlists", "v2", watchlistId] });
  const fetchEntities = (searchParams) => {
    var _a;
    return fetchWatchlistEntries(watchlistId, {
      page: searchParams.page,
      perPage: searchParams.pageSize,
      query: searchParams.query,
      sort: (_a = SORT_FIELDS[searchParams.sort.attributeId]) != null ? _a : "key",
      order: searchParams.sort.direction
    }).then((data) => ({
      list: data.entries.map((entry) => ({ ...entry, id: entry.normalized_value })),
      pagination: { total: data.total },
      attributes: ATTRIBUTES,
      meta: null
    }));
  };
  const keyFn = (searchParams) => ["watchlists", "v2", watchlistId, "entries", searchParams];
  const handleConfirmDelete = () => {
    if (!deletingEntry) {
      return;
    }
    removeWatchlistEntries(watchlistId, [deletingEntry.normalized_value]).then(() => {
      sendTelemetry(TELEMETRY_EVENT_TYPE.WATCHLIST.ENTRIES_DELETED, {
        app_section: "watchlist-details",
        app_action_value: "watchlist-entry-deleted",
        event_details: { watchlist_name: watchlistName, entry_count: 1 }
      });
      UserNotification.success(`Removed "${deletingEntry.value}" from "${watchlistName}".`);
      invalidate();
    }).catch((error) => UserNotification.error(`Removing entry failed: ${error}`)).finally(() => setDeletingEntry(null));
  };
  const renderEntityActions = (entry) => /* @__PURE__ */ React.createElement(MoreActions, null, /* @__PURE__ */ React.createElement(DeleteMenuItem, { onSelect: () => setDeletingEntry(entry) }));
  const bulkSelection = canDelete ? {
    actions: /* @__PURE__ */ React.createElement(WatchlistEntriesBulkActions, { watchlistId, watchlistName, onRemoved: invalidate })
  } : void 0;
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
    PaginatedEntityTable,
    {
      humanName: "watchlist entries",
      tableLayout: DEFAULT_LAYOUT,
      fetchEntities,
      keyFn,
      entityAttributesAreCamelCase: false,
      columnRenderers,
      entityActions: canDelete ? renderEntityActions : void 0,
      bulkSelection,
      searchPlaceholder: "Search entries"
    }
  ), showAddModal && /* @__PURE__ */ React.createElement(
    AddWatchlistEntriesModal,
    {
      watchlistId,
      watchlistName,
      onClose: onCloseAddModal,
      onAdded: invalidate
    }
  ), deletingEntry && /* @__PURE__ */ React.createElement(
    ConfirmDialog,
    {
      title: "Remove watchlist entry",
      show: true,
      onConfirm: handleConfirmDelete,
      onCancel: () => setDeletingEntry(null)
    },
    'Are you sure you want to remove "',
    deletingEntry.value,
    '" from "',
    watchlistName,
    '"?'
  ));
};
export default WatchlistEntries;
